import type {SelectOption, SourceEntityRecord} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';

// The 6 audience dimensions a rule may target. Manager is intentionally
// excluded — it exists on EntityTypeEnum/SourceEntityRecord for other
// modules (course-definition legacy audience, notification rules) but is
// not part of the Assignment Rule builder per the Wave 3 scope.
export const RuleEntityTypes: EntityTypeEnum[] = [
  EntityTypeEnum.Employee,
  EntityTypeEnum.Department,
  EntityTypeEnum.Group,
  EntityTypeEnum.Vendor,
  EntityTypeEnum.Project,
  EntityTypeEnum.Attribute,
];

// Lookup of display names for aggregate-dimension entities (department,
// group, vendor, project), keyed `${entityType}:${entityId}`. Employee and
// Attribute rows resolve to individual people — the sentence builder
// summarizes those by count rather than name to avoid an N+1 lookup for
// what is normally a handful of directly-picked people.
export type SourceEntityNameLookup = Record<string, string>;

export function sourceEntityLookupKey(
  entityType: EntityTypeEnum,
  entityId: number
): string {
  return `${entityType}:${entityId}`;
}

export function buildSourceEntityNameLookup(
  options: Partial<Record<EntityTypeEnum, SelectOption[] | undefined>>
): SourceEntityNameLookup {
  const lookup: SourceEntityNameLookup = {};
  Object.entries(options).forEach(([entityType, entities]) => {
    (entities ?? []).forEach(entity => {
      if (entity.id === undefined || entity.id === null) return;
      lookup[sourceEntityLookupKey(Number(entityType), Number(entity.id))] =
        entity.label ?? String(entity.id);
    });
  });
  return lookup;
}

const entityTypeLabels: Partial<Record<EntityTypeEnum, string>> = {
  [EntityTypeEnum.Employee]: 'person',
  [EntityTypeEnum.Department]: 'department',
  [EntityTypeEnum.Group]: 'group',
  [EntityTypeEnum.Vendor]: 'vendor',
  [EntityTypeEnum.Project]: 'project',
  [EntityTypeEnum.Attribute]: 'targeted person',
};

export function entityTypeLabel(entityType: EntityTypeEnum): string {
  return entityTypeLabels[entityType] ?? 'entity';
}

export function pluralizePeople(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

const selectAllAudienceLabels: Partial<Record<EntityTypeEnum, string>> = {
  [EntityTypeEnum.Employee]: 'All Employees',
  [EntityTypeEnum.Manager]: 'All Managers',
  [EntityTypeEnum.Department]: 'All Departments',
  [EntityTypeEnum.Group]: 'All Groups',
};

export function selectAllAudienceLabel(entityType: EntityTypeEnum): string {
  return selectAllAudienceLabels[entityType] ?? 'All';
}

export interface AudienceFact {
  entityType: EntityTypeEnum;
  label: string;
  value: string;
}

/**
 * One labeled row per audience dimension present on the rule, e.g.
 * `{label: 'Departments', value: 'Operations, Safety'}`. A scannable
 * alternative to generated prose — see docs/plans/training-branch-cleanup.md
 * WS4.
 */
export function describeAudienceFacts(
  sourceEntities: SourceEntityRecord[],
  lookup: SourceEntityNameLookup
): AudienceFact[] {
  if (!sourceEntities?.length) return [];

  const byType = new Map<EntityTypeEnum, SourceEntityRecord[]>();
  sourceEntities.forEach(entity => {
    const list = byType.get(entity.entityType) ?? [];
    list.push(entity);
    byType.set(entity.entityType, list);
  });

  const facts: AudienceFact[] = [];
  RuleEntityTypes.forEach(entityType => {
    const entities = byType.get(entityType);
    if (!entities?.length) return;

    const isNamedGroup =
      entityType !== EntityTypeEnum.Employee &&
      entityType !== EntityTypeEnum.Attribute;

    const value = isNamedGroup
      ? entities
          .map(
            e =>
              lookup[sourceEntityLookupKey(entityType, e.entityId)] ??
              `#${e.entityId}`
          )
          .join(', ')
      : pluralizePeople(entities.length);

    const label = isNamedGroup
      ? `${entityTypeLabel(entityType)}${
          entities.length === 1 ? '' : 's'
        }`.replace(/^./, c => c.toUpperCase())
      : entityType === EntityTypeEnum.Attribute
      ? 'Targeted people'
      : 'People';

    facts.push({entityType, label, value});
  });

  return facts;
}

export const CreateDefaultCourseAssignmentRule = (
  courseDefinitionId: number
) => ({
  courseDefinitionId,
  name: undefined as string | undefined,
  active: true,
  sourceEntities: [] as SourceEntityRecord[],
});
